import { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { styled, keyframes } from "@pigment-css/react";
import { usePopover } from "./usePopover";
import { CheckMark, ItemLabel, SelectItem, SelectPopover, SelectTrigger } from "./selectParts";

export interface InfiniteScrollSelectOption {
  id: string;
  name: string;
}

interface InfiniteScrollSelectProps {
  options: InfiniteScrollSelectOption[];
  value: string;
  disabled?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  onSearch: (searchTerm: string) => void;
  onLoadMore: () => void;
  hasMore?: boolean;
  onChange: (value: string) => void;
}

const SCROLL_THRESHOLD = 100;

const SearchInput = styled("input")(({ theme }) => ({
  width: "100%",
  boxSizing: "border-box",
  padding: `${theme.managementView.spacing[12]} ${theme.managementView.spacing[16]}`,
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[440],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[90],
  backgroundColor: "transparent",
  border: "none",
  borderBottom: `1px solid ${theme.managementView.colors.neutral[8]}`,
  outline: "none",

  "&::placeholder": {
    color: theme.managementView.colors.neutral[56],
  },
}));

const ScrollContainer = styled("div")(() => ({
  maxHeight: "260px",
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: "transparent transparent",

  "&:hover": {
    scrollbarColor: "rgba(19, 19, 19, 0.32) transparent",
  },
}));

const EmptyMessage = styled("div")(({ theme }) => ({
  padding: theme.managementView.spacing[16],
  textAlign: "center",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  color: theme.managementView.colors.neutral[56],
}));

const shimmer = keyframes({
  "0%": { backgroundPosition: "-200px 0" },
  "100%": { backgroundPosition: "200px 0" },
});

const LoadingSkeleton = styled("div")(({ theme }) => ({
  height: "16px",
  width: "120px",
  borderRadius: theme.managementView.radius[10],
  background: `linear-gradient(90deg, ${theme.managementView.colors.neutral[8]} 0%, ${theme.managementView.colors.neutral[4]} 50%, ${theme.managementView.colors.neutral[8]} 100%)`,
  backgroundSize: "400px 100%",
  animation: `${shimmer} 1.5s ease-in-out infinite`,
  margin: `${theme.managementView.spacing[12]} ${theme.managementView.spacing[16]}`,
}));

const Popover = styled(SelectPopover)(() => ({
  minWidth: "200px",
  padding: 0,
  display: "flex",
  flexDirection: "column",
}));

const ItemsContainer = styled("div")(({ theme }) => ({
  padding: theme.managementView.spacing[4],
}));

export const InfiniteScrollSelect = ({
  options,
  value,
  disabled,
  placeholder = "—",
  isLoading = false,
  onSearch,
  onLoadMore,
  hasMore = false,
  onChange,
}: InfiniteScrollSelectProps) => {
  const { isOpen, position, triggerRef, popoverRef, toggle, close } = usePopover();
  const [searchTerm, setSearchTerm] = useState("");
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const selectedLabel = useMemo(() => {
    if (!value) return placeholder;
    return options.find((o) => o.id === value)?.name ?? placeholder;
  }, [options, value, placeholder]);

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    onSearch(term);
  };

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom < SCROLL_THRESHOLD && hasMore && !isLoading) {
      onLoadMore();
    }
  };

  const handleSelect = (id: string) => {
    onChange(id);
    close();
  };

  const handleToggle = () => {
    if (isOpen) close();
    else toggle();
  };

  // Reset search state on every open→closed transition, regardless of cause
  // (selection, trigger click, outside click, Escape). Ref guard avoids firing on mount.
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      setSearchTerm("");
      onSearch("");
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, onSearch]);

  return (
    <>
      <SelectTrigger ref={triggerRef} type="button" disabled={disabled} onClick={handleToggle}>
        {selectedLabel}
      </SelectTrigger>
      {isOpen &&
        createPortal(
          <Popover ref={popoverRef} style={position}>
            <SearchInput
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            <ScrollContainer ref={scrollContainerRef} data-testid="scroll-container" onScroll={handleScroll}>
              <ItemsContainer>
                {!isLoading && options.length === 0 ? (
                  <EmptyMessage>No results</EmptyMessage>
                ) : (
                  options.map((option) => (
                    <SelectItem key={option.id} onClick={() => handleSelect(option.id)}>
                      <ItemLabel>{option.name}</ItemLabel>
                      {option.id === value && <CheckMark />}
                    </SelectItem>
                  ))
                )}
                {isLoading && <LoadingSkeleton data-testid="loading-skeleton" />}
              </ItemsContainer>
            </ScrollContainer>
          </Popover>,
          document.body,
        )}
    </>
  );
};
